import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled, { css } from 'styled-components';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import NumberInput from '@/components/Admin/UI/NumberInput';
import {
   bulkGenerateVouchersApi,
   listDevices,
   type BulkVoucher,
   type VoucherCardStyle,
   type VoucherCodeMode,
   type VoucherPrintLayout,
} from '@/api/vendo';
import {
   CARDS_PER_A4_OPTIONS,
   PREVIEW_SCALE,
   VoucherCard,
   VoucherPrintHost,
   displayCode,
   durationWords,
   sheetsFor,
   a4Grid,
   CardProbe,
   useMeasuredCardHeights,
   type VoucherCardData,
} from './VoucherPrintSheet';
import { compositeStatus } from '@/components/Admin/Vendo/DeviceStatusBadge';
import type { RouterStatus } from '@/components/Admin/Vendo/RouterPill';

/**
 * "+ Generate vouchers" — the Over-the-counter page's wizard (Ray's approved
 * preview, 2026-10-14, then "make it a stepper").
 *
 *   1 Vendos          tick list (online first, a dot per vendo, offline hidden
 *                     until "Show offline vendos"), "All vendos" master, search
 *   2 Rate            typed by hand: ₱ + duration + unit, or ☑ FREE
 *   3 Codes & options random / numbered / type my own · qty · prefix ·
 *                     batch label · expires · speed · pause · moveable
 *   4 Print layout    store name · logo · cards per A4 · card style ·
 *                     live preview of one card → Generate & print
 *
 * Next is off until the step is valid; done steps are ticked and clickable;
 * every step's state survives Back; Cancel everywhere; the modal NEVER closes
 * on an outside click (a form modal — rule from 2026-09-29).
 */

export const OWN_CODE_RE = /^[A-Z0-9-]{4,16}$/;
export const STEPS = ['Vendos', 'Rate', 'Codes & options', 'Print layout'] as const;
export const STORE_NAME_KEY = 'smartfi.print.storeName';
const MAX_CODES = 500;

export type DurationUnit = 'minutes' | 'hours' | 'days';
export function toMinutes(n: number, unit: DurationUnit): number {
   const v = Math.max(0, Number(n) || 0);
   return Math.round(unit === 'days' ? v * 1440 : unit === 'hours' ? v * 60 : v);
}

/** Own codes, as typed — one per line, upper-cased, blanks dropped. */
export function parseOwnCodes(text: string): string[] {
   return text.split('\n').map((s) => s.trim().toUpperCase()).filter(Boolean);
}
/** What the hint under the textarea says, and whether the step may go on. */
export function ownCodesCheck(codes: string[]): { ok: boolean; text: string; invalid: string[]; duplicates: string[] } {
   const invalid = codes.filter((c) => !OWN_CODE_RE.test(c));
   // The portal ignores dashes, so NENA-100 and NENA100 are one code.
   const seen = new Set<string>();
   const duplicates: string[] = [];
   for (const c of codes) {
      const key = c.replace(/-/g, '');
      if (seen.has(key)) duplicates.push(c);
      else seen.add(key);
   }
   const n = codes.length;
   const parts = [`${n} code${n === 1 ? '' : 's'}`];
   if (invalid.length) parts.push(`${invalid.length} invalid (letters, numbers, dashes, 4–16 characters)`);
   if (duplicates.length) parts.push(`${duplicates.length} duplicate`);
   if (n > MAX_CODES) parts.push(`at most ${MAX_CODES} in one run`);
   if (n && !invalid.length && !duplicates.length && n <= MAX_CODES) parts.push('all look fine');
   return { ok: n > 0 && !invalid.length && !duplicates.length && n <= MAX_CODES, text: parts.join(' · '), invalid, duplicates };
}

export interface WizardVendo {
   _id: string;
   name: string;
   deviceId: string;
   parent?: string | null;
   status?: 'online' | 'offline' | 'error';
   routerStatus?: RouterStatus;
}

/**
 * Can this vendo take the codes right now? The SAME rule the Devices page
 * card uses (NodeMCU heartbeat × router reachability): the card's Online
 * and Degraded both mean the router is reachable, so the codes land at
 * once; Offline means they land on its next check-in.
 */
export type VendoReach = 'online' | 'degraded' | 'offline';
export function vendoReach(v: Pick<WizardVendo, 'status' | 'routerStatus'>): VendoReach {
   const heartbeat = v.status === 'online' ? 'online' : 'offline';
   const router = v.routerStatus ?? (v.status === 'online' ? 'connected' : 'disconnected');
   return compositeStatus(heartbeat, router);
}
const REACH_RANK: Record<VendoReach, number> = { online: 0, degraded: 1, offline: 2 };

/**
 * Order the tick list: main vendos first (each followed by its sub-vendos,
 * as on the Devices page), reachable groups before offline ones, and inside
 * a group the reachable sub-vendos first. `showOffline` false drops the
 * offline rows.
 */
export function orderVendos(all: WizardVendo[], showOffline: boolean): WizardVendo[] {
   const keep = (v: WizardVendo) => showOffline || vendoReach(v) !== 'offline';
   const rank = (v: WizardVendo) => REACH_RANK[vendoReach(v)];
   const byReach = (a: WizardVendo, b: WizardVendo) => rank(a) - rank(b);
   const mains = all.filter((d) => !d.parent).slice().sort(byReach);
   const out: WizardVendo[] = [];
   for (const m of mains) {
      if (keep(m)) out.push(m);
      for (const sub of all.filter((d) => d.parent === m._id).sort(byReach)) if (keep(sub)) out.push(sub);
   }
   for (const sub of all.slice().sort(byReach)) {
      if (sub.parent && !mains.some((m) => m._id === sub.parent) && keep(sub)) out.push(sub);
   }
   return out;
}

/* ── logo: shrink to fit instead of refusing ─────────────────────────── */

/** Long side after resizing. */
export const LOGO_MAX_SIDE = 512;
/** Stay well under the server's 400 KB data-URL cap. */
export const LOGO_MAX_DATA_URL = 350 * 1024;
const JPEG_QUALITIES = [0.85, 0.75, 0.65, 0.55, 0.45, 0.35];

export interface ResizedLogo {
   dataUrl: string;
   width: number;
   height: number;
   /** False when the picture was already small enough and kept as is. */
   resized: boolean;
}

function loadImage(file: Blob): Promise<HTMLImageElement> {
   return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that picture')); };
      img.src = url;
   });
}

function readAsDataUrl(file: Blob): Promise<string> {
   return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Could not read that file'));
      reader.readAsDataURL(file);
   });
}

/** Does the drawn picture have any see-through pixel? Samples the canvas. */
function hasTransparency(ctx: CanvasRenderingContext2D, w: number, h: number): boolean {
   try {
      const { data } = ctx.getImageData(0, 0, w, h);
      // Every 4th pixel is plenty to spot a transparent background.
      for (let i = 3; i < data.length; i += 16) if (data[i] < 250) return true;
   } catch {
      /* tainted or unsupported canvas — assume opaque */
   }
   return false;
}

/**
 * Shrink a picked logo so it fits the card and the server: at most
 * LOGO_MAX_SIDE px on the long side, JPEG q0.85 (PNG only when the source
 * is see-through AND the PNG stays small), quality lowered in steps until
 * the data URL is under LOGO_MAX_DATA_URL. Only a non-image is refused.
 */
export async function resizeLogo(file: File | Blob): Promise<ResizedLogo> {
   if (!/^image\//i.test(file.type)) throw new Error('That file is not a picture — pick a PNG or JPG');
   const img = await loadImage(file);
   const srcW = img.naturalWidth || img.width;
   const srcH = img.naturalHeight || img.height;
   if (!srcW || !srcH) throw new Error('Could not read that picture');

   // Already small in pixels and bytes: keep it exactly as picked.
   if (Math.max(srcW, srcH) <= LOGO_MAX_SIDE && file.size <= LOGO_MAX_DATA_URL * 0.7) {
      const dataUrl = await readAsDataUrl(file);
      if (dataUrl.length <= LOGO_MAX_DATA_URL) return { dataUrl, width: srcW, height: srcH, resized: false };
   }

   let scale = Math.min(1, LOGO_MAX_SIDE / Math.max(srcW, srcH));
   for (let round = 0; round < 4; round++) {
      const w = Math.max(1, Math.round(srcW * scale));
      const h = Math.max(1, Math.round(srcH * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('This browser cannot resize pictures');
      ctx.drawImage(img, 0, 0, w, h);

      const seeThrough = /png|gif|webp|svg/i.test(file.type) && hasTransparency(ctx, w, h);
      if (seeThrough) {
         const png = canvas.toDataURL('image/png');
         if (png.length <= LOGO_MAX_DATA_URL) return { dataUrl: png, width: w, height: h, resized: true };
      }
      // JPEG has no see-through: paint white behind the picture first.
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'source-over';
      for (const q of JPEG_QUALITIES) {
         const jpg = canvas.toDataURL('image/jpeg', q);
         if (jpg.length <= LOGO_MAX_DATA_URL) return { dataUrl: jpg, width: w, height: h, resized: true };
      }
      // Still too big at the lowest quality: shrink the picture and go again.
      scale *= 0.7;
   }
   throw new Error('That picture is too detailed to fit — try a simpler logo');
}

export interface WizardState {
   vendoIds: string[];
   free: boolean;
   price: number;
   duration: number;
   unit: DurationUnit;
   codeMode: VoucherCodeMode;
   quantity: number;
   prefix: string;
   ownText: string;
   batchLabel: string;
   /** 0 = never */
   expiresDays: 0 | 30 | 60 | 90;
   /** null = the vendo's default speed */
   speedMbps: number | null;
   /** null = the vendo's default pause validity */
   pauseMinutes: number | null;
   moveable: boolean;
   storeName: string;
   logoDataUrl: string;
   cardsPerA4: 8 | 12 | 20 | 30;
   cardStyle: VoucherCardStyle;
}

export function initialState(): WizardState {
   let storeName = '';
   try { storeName = localStorage.getItem(STORE_NAME_KEY) || ''; } catch { /* private mode */ }
   return {
      vendoIds: [],
      free: false,
      // A real starting value, not a grey placeholder that reads like one
      // (Ray 2026-10-18): the operator edits it, never wonders if it is set.
      price: 20,
      duration: 3,
      unit: 'hours',
      codeMode: 'random',
      quantity: 20,
      prefix: '',
      ownText: '',
      batchLabel: '',
      expiresDays: 30,
      speedMbps: null,
      pauseMinutes: null,
      moveable: false,
      storeName,
      logoDataUrl: '',
      cardsPerA4: 20, // Small — 4 across (Ray 2026-10-18)
      cardStyle: 'classic',
   };
}

export function countFor(s: WizardState): number {
   return s.codeMode === 'own' ? parseOwnCodes(s.ownText).length : Math.max(0, Math.floor(s.quantity));
}

/** Is step `i` (0-based) complete enough to move past? */
export function stepValid(s: WizardState, i: number): boolean {
   switch (i) {
      case 0:
         return s.vendoIds.length > 0;
      case 1:
         return toMinutes(s.duration, s.unit) >= 1 && (s.free || s.price > 0);
      case 2: {
         if (s.prefix && !/^[A-Z0-9]{1,8}$/.test(s.prefix)) return false;
         if (s.codeMode === 'own') return ownCodesCheck(parseOwnCodes(s.ownText)).ok;
         return s.quantity >= 1 && s.quantity <= MAX_CODES;
      }
      default:
         return true;
   }
}

export function vendoWords(picked: string[], all: WizardVendo[]): string {
   if (!picked.length) return '';
   if (all.length && picked.length === all.length) return `all ${all.length} vendos`;
   if (picked.length === 1) return all.find((v) => v._id === picked[0])?.name ?? '1 vendo';
   return `${picked.length} vendos`;
}

/** The footer line: "20 × ₱20 · 3 hours each · on all 4 vendos = ₱400 printed value". */
export function summaryLine(s: WizardState, all: WizardVendo[]): string {
   const n = countFor(s);
   const d = `${s.duration} ${s.unit}`;
   if (!s.vendoIds.length) return 'Tick at least one vendo.';
   if (!n) return s.codeMode === 'own' ? 'Type at least one code.' : 'Type how many vouchers.';
   if (!s.free && !(s.price > 0)) return 'Type a price (or tick FREE).';
   const on = vendoWords(s.vendoIds, all);
   if (s.free) return `${n} FREE voucher${n === 1 ? '' : 's'} · ${d} each · on ${on} — cards print "FREE"`;
   return `${n} × ₱${s.price} · ${d} each · on ${on} = ₱${(n * s.price).toLocaleString()} printed value`;
}

export function sampleCode(s: WizardState): string {
   const p = (s.prefix || 'SARI').toUpperCase();
   if (s.codeMode === 'numbered') return `${p}-001`;
   if (s.codeMode === 'own') return parseOwnCodes(s.ownText)[0] || 'YOUR-CODE';
   return `${p}-7K2M9`;
}

const CODE_MODE_HINT: Record<VoucherCodeMode, string> = {
   random: 'Prefix + 5 random letters/numbers, e.g. SARI-7K2M9',
   numbered: 'Prefix + running number, e.g. SARI-001, SARI-002 … easy to track by hand',
   own: 'Paste or type the exact codes you want. We check they are unused.',
};

/* ────────────────────────────────────────────────────────────────────── */

export function GenerateVouchersWizard({
   onClose,
   onGenerated,
   fixedVendoIds,
}: {
   onClose: () => void;
   /** Fired once the run is minted (the page refreshes its table). */
   onGenerated?: (count: number) => void;
   /** Buyer's sub-login page (Ray 2026-10-18): the run is for THIS vendo only —
    *  the Vendos step is pre-answered and skipped. */
   fixedVendoIds?: string[];
}) {
   const queryClient = useQueryClient();
   const fixed = !!fixedVendoIds?.length;
   const [step, setStep] = useState(fixed ? 1 : 0);
   const [s, setS] = useState<WizardState>(() => ({ ...initialState(), ...(fixedVendoIds?.length ? { vendoIds: fixedVendoIds } : {}) }));
   const set = <K extends keyof WizardState>(k: K, v: WizardState[K]) => setS((p) => ({ ...p, [k]: v }));
   const [search, setSearch] = useState('');
   const [showOffline, setShowOffline] = useState(false);
   const [submitting, setSubmitting] = useState(false);
   const [done, setDone] = useState<{ vouchers: BulkVoucher[]; layout: VoucherPrintLayout } | null>(null);
   const [logoInfo, setLogoInfo] = useState<ResizedLogo | null>(null);
   const [logoBusy, setLogoBusy] = useState(false);

   const { data: devicesData, isLoading: vendosLoading } = useQuery({
      queryKey: ['otc-wizard-vendos'],
      queryFn: () => listDevices(),
   });
   const allVendos: WizardVendo[] = useMemo(() => (devicesData?.devices ?? []) as WizardVendo[], [devicesData]);
   const offlineCount = useMemo(() => allVendos.filter((v) => vendoReach(v) === 'offline').length, [allVendos]);
   // The list as filtered: online first, offline rows only when asked for.
   // "All vendos" and its count follow this same list.
   const vendos = useMemo(() => orderVendos(allVendos, showOffline), [allVendos, showOffline]);
   const visibleVendos = useMemo(() => {
      const n = search.trim().toLowerCase();
      if (!n) return vendos;
      return vendos.filter((v) => v.name.toLowerCase().includes(n) || v.deviceId.toLowerCase().includes(n));
   }, [vendos, search]);
   const visibleIds = useMemo(() => new Set(visibleVendos.map((v) => v._id)), [visibleVendos]);
   const allTicked = vendos.length > 0 && vendos.every((v) => s.vendoIds.includes(v._id));

   const toggleVendo = (id: string) =>
      setS((p) => ({
         ...p,
         vendoIds: p.vendoIds.includes(id) ? p.vendoIds.filter((x) => x !== id) : [...p.vendoIds, id],
      }));
   // Ticks every LISTED vendo; unticking clears the listed ones and keeps a
   // hidden (offline) vendo that was ticked before the filter changed.
   const toggleAll = () =>
      setS((p) => {
         const listed = new Set(vendos.map((v) => v._id));
         return {
            ...p,
            vendoIds: allTicked
               ? p.vendoIds.filter((id) => !listed.has(id))
               : Array.from(new Set([...p.vendoIds, ...vendos.map((v) => v._id)])),
         };
      });

   const valid = STEPS.map((_, i) => stepValid(s, i));
   /** A step is reachable when every step before it is valid. */
   const reachable = (i: number) => valid.slice(0, i).every(Boolean);
   const last = step === STEPS.length - 1;
   const canGo = valid.slice(0, STEPS.length - 1).every(Boolean);
   // Real printed-card heights (per size) from this page's styles, so the
   // "per A4" numbers here are the numbers that print.
   const [cardMm, setCardMm] = useMeasuredCardHeights();

   const n = countFor(s);
   const minutes = toMinutes(s.duration, s.unit);
   const previewCard: VoucherCardData = {
      code: sampleCode(s),
      price: s.price || 20,
      duration: minutes || 180,
      free: s.free,
      validityText: s.expiresDays ? `valid ${s.expiresDays} days` : '',
   };

   const clearLogo = () => { set('logoDataUrl', ''); setLogoInfo(null); };
   const handleLogoPick = async (file: File | null) => {
      if (!file) { clearLogo(); return; }
      setLogoBusy(true);
      try {
         const r = await resizeLogo(file);
         set('logoDataUrl', r.dataUrl);
         setLogoInfo(r);
      } catch (err: any) {
         toast.error(err?.message ?? 'Could not read that picture');
      } finally {
         setLogoBusy(false);
      }
   };

   const generate = async () => {
      if (submitting || !canGo) return;
      setSubmitting(true);
      try {
         const own = parseOwnCodes(s.ownText);
         const layout: VoucherPrintLayout = {
            storeName: s.storeName.trim(),
            logoDataUrl: s.logoDataUrl,
            cardsPerA4: s.cardsPerA4,
            cardStyle: s.cardStyle,
         };
         const r = await bulkGenerateVouchersApi({
            deviceIds: s.vendoIds,
            free: s.free,
            custom: { price: s.free ? 0 : s.price, durationMinutes: minutes },
            codeMode: s.codeMode,
            ...(s.codeMode === 'own' ? { codes: own } : { copies: n }),
            prefix: s.prefix,
            // Random part: 5 after a prefix (SARI-7K2M9); 8 when there is no
            // prefix so a bare code is not guessable.
            codeLength: s.prefix ? 5 : 8,
            batchLabel: s.batchLabel.trim(),
            validUntil: s.expiresDays ? new Date(Date.now() + s.expiresDays * 86400000).toISOString() : null,
            downloadMbps: s.speedMbps,
            uploadMbps: s.speedMbps,
            validityMinutes: s.pauseMinutes,
            moveable: s.moveable,
            print: layout,
         });
         try { localStorage.setItem(STORE_NAME_KEY, layout.storeName); } catch { /* non-fatal */ }
         setDone({ vouchers: r.vouchers, layout: r.print ?? layout });
         queryClient.invalidateQueries({ queryKey: ['otc-vouchers'] });
         queryClient.invalidateQueries({ queryKey: ['otc-voucher-stats'] });
         queryClient.invalidateQueries({ queryKey: ['device-vouchers'] });
         onGenerated?.(r.vouchers.length);
         toast.success(`${r.vouchers.length} voucher${r.vouchers.length === 1 ? '' : 's'} generated`);
      } catch (err: any) {
         const body = err?.response?.data;
         toast.error(body?.error ?? err?.message ?? 'Could not generate vouchers');
         // A code the server refused: jump back to the codes step so it can be fixed.
         if (Array.isArray(body?.codes) && body.codes.length) setStep(2);
      } finally {
         setSubmitting(false);
      }
   };

   // Own codes were typed with dashes; the server stores them without. Print
   // them the way they were typed.
   const typedByKey = useMemo(() => {
      const m = new Map<string, string>();
      for (const c of parseOwnCodes(s.ownText)) m.set(c.replace(/-/g, ''), c);
      return m;
   }, [s.ownText]);
   const printCards: VoucherCardData[] = (done?.vouchers ?? []).map((v) => ({
      code: s.codeMode === 'own' ? typedByKey.get(v.code) ?? v.code : displayCode(v.code, s.prefix),
      price: v.price,
      duration: v.duration,
      free: !!v.free,
      validityText: v.validUntil
         ? `valid until ${new Date(v.validUntil).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })}`
         : '',
   }));

   // Print once the sheets are in the document.
   const printedRef = useRef(false);
   useEffect(() => {
      if (!done || printedRef.current) return;
      printedRef.current = true;
      const t = setTimeout(() => { try { window.print(); } catch { /* jsdom */ } }, 300);
      return () => clearTimeout(t);
   }, [done]);

   // Esc = Cancel. Outside click = nothing (form modal).
   useEffect(() => {
      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
      window.addEventListener('keydown', onKey);
      return () => window.removeEventListener('keydown', onKey);
   }, [onClose]);

   const body = (
      <Overlay role="presentation" data-testid="otc-wizard-overlay">
         <Modal role="dialog" aria-modal="true" aria-labelledby="otc-wizard-title">
            <Head>
               <h2 id="otc-wizard-title">{done ? 'Vouchers ready' : 'Generate vouchers'}</h2>
               <Close type="button" aria-label="Close" onClick={onClose}>×</Close>
            </Head>

            {done ? (
               <>
                  <Body>
                     <DoneBox>
                        <b>{done.vouchers.length} voucher{done.vouchers.length === 1 ? '' : 's'} generated</b>
                        <span>
                           {sheetsFor(done.vouchers.length, done.layout.cardsPerA4, done.layout.cardStyle, cardMm[done.layout.cardsPerA4])} A4 sheet
                           {sheetsFor(done.vouchers.length, done.layout.cardsPerA4, done.layout.cardStyle, cardMm[done.layout.cardsPerA4]) === 1 ? '' : 's'} ·{' '}
                           {a4Grid(done.layout.cardsPerA4, done.layout.cardStyle, cardMm[done.layout.cardsPerA4]).perSheet} cards each. The print window opens by itself — if it did not, press Print again.
                        </span>
                     </DoneBox>
                     <PreviewWrap>
                        <VoucherCard card={printCards[0] ?? previewCard} layout={done.layout} logoDataUrl={done.layout.logoDataUrl} scale={PREVIEW_SCALE[done.layout.cardsPerA4] ?? 1} />
                     </PreviewWrap>
                  </Body>
                  <Foot>
                     <Summary>{summaryLine(s, allVendos)}</Summary>
                     <Btns>
                        <Ghost type="button" onClick={() => window.print()}>Print again</Ghost>
                        <Primary type="button" onClick={onClose}>Done</Primary>
                     </Btns>
                  </Foot>
                  <VoucherPrintHost cards={printCards} layout={done.layout} />
               </>
            ) : (
               <>
                  <Timeline aria-label="Steps">
                     {STEPS.map((label, i) => {
                        const doneStep = i < step && valid[i];
                        const clickable = i !== step && reachable(i);
                        return (
                           <TimelineStep
                              key={label}
                              type="button"
                              style={fixed && i === 0 ? { display: 'none' } : undefined}
                              $current={i === step}
                              $done={doneStep}
                              disabled={!clickable}
                              aria-current={i === step ? 'step' : undefined}
                              onClick={() => clickable && setStep(i)}
                              data-testid={`otc-step-${i + 1}`}
                           >
                              <Dot $current={i === step} $done={doneStep}>{doneStep ? '✓' : i + 1}</Dot>
                              <span>{label}</span>
                           </TimelineStep>
                        );
                     })}
                  </Timeline>

                  <Body>
                     {step === 0 && (
                        <Section>
                           <SecTitle>Vendos</SecTitle>
                           <Field>
                              <label>Which vendos will these codes work on?</label>
                              <Hint>Tick one, several, or all. A code works on every vendo you tick.</Hint>
                              <VendoList data-testid="otc-vendo-list">
                                 <VendoRow $all>
                                    <input type="checkbox" checked={allTicked} onChange={toggleAll} disabled={!vendos.length} aria-label="All vendos" />
                                    <b>All vendos</b>
                                    <Hint>({vendos.length})</Hint>
                                 </VendoRow>
                                 <ListTools>
                                    <VendoSearch
                                       placeholder="Search vendos…"
                                       value={search}
                                       onChange={(e) => setSearch(e.target.value)}
                                       aria-label="Search vendos"
                                    />
                                    <Toggle $small>
                                       <input type="checkbox" checked={showOffline} onChange={(e) => setShowOffline(e.target.checked)} />
                                       Show offline vendos{offlineCount ? ` (${offlineCount})` : ''}
                                    </Toggle>
                                 </ListTools>
                                 {vendosLoading && <SkeletonLines lines={3} />}
                                 {!vendosLoading && !allVendos.length && <Hint style={{ padding: '8px 12px' }}>No vendos yet — register one first.</Hint>}
                                 {!vendosLoading && allVendos.length > 0 && !vendos.length && (
                                    <Hint style={{ padding: '8px 12px' }}>All your vendos are offline right now — tick "Show offline vendos" to list them.</Hint>
                                 )}
                                 {visibleVendos.map((v) => {
                                    const reach = vendoReach(v);
                                    return (
                                       <VendoRow key={v._id} $sub={!!v.parent} data-reach={reach}>
                                          <input
                                             type="checkbox"
                                             checked={s.vendoIds.includes(v._id)}
                                             onChange={() => toggleVendo(v._id)}
                                             aria-label={v.name}
                                          />
                                          {v.parent && visibleIds.has(v.parent) ? '↳ ' : ''}{v.name}
                                          <ReachDot
                                             $reach={reach}
                                             title={reach === 'online' ? 'Online' : reach === 'degraded' ? 'Router reachable, coin slot not reporting' : 'Offline'}
                                             aria-label={reach === 'offline' ? 'offline' : 'online'}
                                          />
                                          <small>{v.deviceId}</small>
                                       </VendoRow>
                                    );
                                 })}
                              </VendoList>
                              <Hint>
                                 Offline vendos receive the codes on their next check-in (codes are redeemed on the router, so they work even when the internet is down).
                              </Hint>
                              <Picked data-testid="otc-picked">
                                 {s.vendoIds.length ? (
                                    <>
                                       Works on <b>{vendoWords(s.vendoIds, allVendos)}</b>
                                       {s.vendoIds.length > 1 && s.vendoIds.length < allVendos.length
                                          ? `: ${s.vendoIds.map((id) => allVendos.find((v) => v._id === id)?.name ?? '').filter(Boolean).join(', ')}`
                                          : ''}
                                    </>
                                 ) : (
                                    'No vendo ticked yet.'
                                 )}
                              </Picked>
                           </Field>
                        </Section>
                     )}

                     {step === 1 && (
                        <Section>
                           <SecTitle>Rate — type it yourself</SecTitle>
                           <FreeRow>
                              <input type="checkbox" checked={s.free} onChange={(e) => set('free', e.target.checked)} />
                              <b>FREE voucher</b>
                              <Hint>— no price; the printed card says FREE</Hint>
                           </FreeRow>
                           <RateRow $cols={s.free ? 2 : 3} data-testid="otc-rate-row" data-cols={s.free ? 2 : 3}>
                              {!s.free && (
                                 <Field>
                                    <label htmlFor="otc-price">Price (₱)</label>
                                    <NumberInput id="otc-price" as={Input} value={s.price || null} onChange={(v) => set('price', v)} min={1} />
                                 </Field>
                              )}
                              <Field>
                                 <label htmlFor="otc-duration">Duration</label>
                                 <NumberInput id="otc-duration" as={Input} value={s.duration || null} onChange={(v) => set('duration', v)} min={1} />
                              </Field>
                              <Field>
                                 <label htmlFor="otc-unit">Unit</label>
                                 <Select id="otc-unit" value={s.unit} onChange={(e) => set('unit', e.target.value as DurationUnit)}>
                                    <option value="minutes">minutes</option>
                                    <option value="hours">hours</option>
                                    <option value="days">days</option>
                                 </Select>
                              </Field>
                           </RateRow>
                           <Hint>
                              {s.free
                                 ? `Each code gives ${durationWords(minutes) || '…'} of internet, free.`
                                 : s.price > 0
                                 ? `Each code sells for ₱${s.price} and gives ${durationWords(minutes) || '…'} of internet.`
                                 : 'Type the price customers pay for one code, and how long it lasts.'}
                           </Hint>
                        </Section>
                     )}

                     {step === 2 && (
                        <Section>
                           <SecTitle>Codes &amp; options</SecTitle>
                           <Field>
                              <label>Codes</label>
                              <Choices role="radiogroup" aria-label="Code mode">
                                 {(['random', 'numbered', 'own'] as VoucherCodeMode[]).map((m) => (
                                    <Choice key={m} type="button" role="radio" aria-checked={s.codeMode === m} $on={s.codeMode === m} onClick={() => set('codeMode', m)}>
                                       {m === 'random' ? 'Random' : m === 'numbered' ? 'Numbered' : 'Type my own'}
                                    </Choice>
                                 ))}
                              </Choices>
                              <Hint>{CODE_MODE_HINT[s.codeMode]}</Hint>
                           </Field>
                           {s.codeMode === 'own' ? (
                              <Field>
                                 <label htmlFor="otc-own">Your codes — one per line</label>
                                 <Textarea
                                    id="otc-own"
                                    rows={5}
                                    value={s.ownText}
                                    onChange={(e) => set('ownText', e.target.value)}
                                    placeholder={'NENA-100\nNENA-200\nPROMO2026'}
                                    spellCheck={false}
                                 />
                                 <Hint data-testid="otc-own-hint">
                                    {s.ownText.trim()
                                       ? ownCodesCheck(parseOwnCodes(s.ownText)).text
                                       : '0 codes · letters, numbers and dashes only · each must be unused'}
                                 </Hint>
                              </Field>
                           ) : (
                              <Grid3>
                                 <Field>
                                    <label htmlFor="otc-qty">Quantity</label>
                                    <NumberInput id="otc-qty" as={Input} value={s.quantity || null} onChange={(v) => set('quantity', v)} min={1} max={MAX_CODES} />
                                 </Field>
                                 <Field>
                                    <label htmlFor="otc-prefix">Code prefix</label>
                                    <Input
                                       id="otc-prefix"
                                       value={s.prefix}
                                       maxLength={8}
                                       placeholder="SARI"
                                       onChange={(e) => set('prefix', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                                    />
                                 </Field>
                                 <Field>
                                    <label htmlFor="otc-batch">Batch label</label>
                                    <Input id="otc-batch" value={s.batchLabel} maxLength={40} placeholder="Sari-sari Oct" onChange={(e) => set('batchLabel', e.target.value)} />
                                 </Field>
                              </Grid3>
                           )}
                           {s.codeMode === 'own' && (
                              <Field>
                                 <label htmlFor="otc-batch2">Batch label</label>
                                 <Input id="otc-batch2" value={s.batchLabel} maxLength={40} placeholder="Sari-sari Oct" onChange={(e) => set('batchLabel', e.target.value)} />
                              </Field>
                           )}
                           <Grid3>
                              <Field>
                                 <label htmlFor="otc-expires">Expires after</label>
                                 <Select id="otc-expires" value={String(s.expiresDays)} onChange={(e) => set('expiresDays', Number(e.target.value) as WizardState['expiresDays'])}>
                                    <option value="0">Never</option>
                                    <option value="30">30 days</option>
                                    <option value="60">60 days</option>
                                    <option value="90">90 days</option>
                                 </Select>
                              </Field>
                              <Field>
                                 <label htmlFor="otc-speed">Speed limit</label>
                                 <Select id="otc-speed" value={s.speedMbps == null ? '' : String(s.speedMbps)} onChange={(e) => set('speedMbps', e.target.value === '' ? null : Number(e.target.value))}>
                                    <option value="">Vendo's default speed</option>
                                    <option value="3">3 Mbps</option>
                                    <option value="5">5 Mbps</option>
                                    <option value="10">10 Mbps</option>
                                    <option value="20">20 Mbps</option>
                                 </Select>
                              </Field>
                              <Field>
                                 <label htmlFor="otc-pause">Pause validity</label>
                                 <Select id="otc-pause" value={s.pauseMinutes == null ? '' : String(s.pauseMinutes)} onChange={(e) => set('pauseMinutes', e.target.value === '' ? null : Number(e.target.value))}>
                                    <option value="">Vendo's default</option>
                                    <option value="1440">1 day</option>
                                    <option value="4320">3 days</option>
                                    <option value="10080">7 days</option>
                                 </Select>
                              </Field>
                           </Grid3>
                           <Toggle>
                              <input type="checkbox" checked={s.moveable} onChange={(e) => set('moveable', e.target.checked)} />
                              Moveable — the customer can carry this voucher to another phone
                           </Toggle>
                        </Section>
                     )}

                     {step === 3 && (
                        <Section>
                           <SecTitle>Print layout</SecTitle>
                           <Grid2>
                              <Field>
                                 <label htmlFor="otc-store">Store name on the card</label>
                                 <Input id="otc-store" value={s.storeName} maxLength={60} placeholder="Aling Nena's Sari-sari" onChange={(e) => set('storeName', e.target.value)} />
                              </Field>
                              <Field>
                                 <label htmlFor="otc-logo">Logo (optional)</label>
                                 <Input id="otc-logo" type="file" accept="image/*" disabled={logoBusy} onChange={(e) => handleLogoPick(e.target.files?.[0] ?? null)} />
                                 <Hint data-testid="otc-logo-hint">
                                    {logoBusy
                                       ? 'Shrinking the picture…'
                                       : logoInfo && s.logoDataUrl
                                       ? `Logo ${logoInfo.resized ? 'resized to' : 'kept at'} ${logoInfo.width}×${logoInfo.height}`
                                       : 'PNG or JPG — big pictures are shrunk to fit; shows top-left of each card'}
                                    {s.logoDataUrl ? ' · ' : ''}
                                    {s.logoDataUrl && <LinkBtn type="button" onClick={clearLogo}>remove</LinkBtn>}
                                 </Hint>
                              </Field>
                           </Grid2>
                           <Grid2>
                              <Field>
                                 <label htmlFor="otc-per-a4">Card size</label>
                                 <Select id="otc-per-a4" value={String(s.cardsPerA4)} onChange={(e) => set('cardsPerA4', Number(e.target.value) as WizardState['cardsPerA4'])}>
                                    {CARDS_PER_A4_OPTIONS.map((o) => (
                                       <option key={o.value} value={o.value}>
                                          {o.label} · {a4Grid(o.value, s.cardStyle, cardMm[o.value]).perSheet} per A4
                                       </option>
                                    ))}
                                 </Select>
                              </Field>
                              <Field>
                                 <label>Card style</label>
                                 <Choices role="radiogroup" aria-label="Card style">
                                    {([['classic', 'Classic ticket'], ['strip', 'Compact strip'], ['bold', 'Bold']] as [VoucherCardStyle, string][]).map(([v, label]) => (
                                       <Choice key={v} type="button" role="radio" aria-checked={s.cardStyle === v} $on={s.cardStyle === v} onClick={() => set('cardStyle', v)}>
                                          {label}
                                       </Choice>
                                    ))}
                                 </Choices>
                              </Field>
                           </Grid2>
                           <Field>
                              <label>Preview — this is one card as it prints</label>
                              <PreviewWrap>
                                 <VoucherCard
                                    card={previewCard}
                                    layout={{ storeName: s.storeName, cardStyle: s.cardStyle }}
                                    logoDataUrl={s.logoDataUrl}
                                    scale={PREVIEW_SCALE[s.cardsPerA4] ?? 1}
                                 />
                              </PreviewWrap>
                              <CardProbe card={previewCard} layout={{ storeName: s.storeName, cardStyle: s.cardStyle, logoDataUrl: s.logoDataUrl }} onMeasure={setCardMm} />
                              <Hint data-testid="otc-sheets">
                                 {a4Grid(s.cardsPerA4, s.cardStyle, cardMm[s.cardsPerA4]).perSheet} cards per A4 · {n} voucher{n === 1 ? '' : 's'} = {sheetsFor(n, s.cardsPerA4, s.cardStyle, cardMm[s.cardsPerA4])} sheet{sheetsFor(n, s.cardsPerA4, s.cardStyle, cardMm[s.cardsPerA4]) === 1 ? '' : 's'}
                              </Hint>
                           </Field>
                        </Section>
                     )}
                  </Body>

                  <Foot>
                     <Summary data-testid="otc-summary">{last ? summaryLine(s, allVendos) : `Step ${step + 1} of ${STEPS.length}`}</Summary>
                     <Btns>
                        <Ghost type="button" onClick={onClose}>Cancel</Ghost>
                        {step > 0 && (
                           <Ghost type="button" disabled={fixed && step === 1} onClick={() => setStep(step - 1)}>Back</Ghost>
                        )}
                        {!last ? (
                           <Primary type="button" disabled={!valid[step]} onClick={() => setStep(step + 1)}>
                              Next
                           </Primary>
                        ) : (
                           <Primary type="button" disabled={!canGo || submitting} onClick={generate}>
                              {submitting ? 'Generating…' : 'Generate & print'}
                           </Primary>
                        )}
                     </Btns>
                  </Foot>
               </>
            )}
         </Modal>
      </Overlay>
   );
   return createPortal(body, document.body);
}

export default GenerateVouchersWizard;

/* ── styles (the approved preview, in the admin's tokens) ───────────── */

const Overlay = styled.div`
   position: fixed;
   inset: 0;
   background: rgba(10, 13, 24, 0.55);
   display: flex;
   align-items: center;
   justify-content: center;
   padding: 16px;
   z-index: 2000;
   @media print {
      display: none;
   }
`;

const Modal = styled.div`
   background: ${colors.WHITE};
   color: ${colors.GRAY1};
   width: 100%;
   max-width: 640px;
   border-radius: 14px;
   box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
   max-height: 92vh;
   display: flex;
   flex-direction: column;
   overflow: hidden;
`;

const Head = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   padding: 16px 20px;
   border-bottom: 1px solid ${colors.GRAY13};
   flex-shrink: 0;
   h2 {
      margin: 0;
      font-size: 17px;
   }
`;

const Close = styled.button`
   border: 0;
   background: transparent;
   font-size: 22px;
   line-height: 1;
   cursor: pointer;
   color: ${colors.GRAY3};
`;

const Timeline = styled.div`
   display: grid;
   grid-template-columns: repeat(4, 1fr);
   gap: 4px;
   padding: 12px 20px 0;
   flex-shrink: 0;
`;

const TimelineStep = styled.button<{ $current: boolean; $done: boolean }>`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 6px;
   padding: 6px 4px 10px;
   border: 0;
   background: transparent;
   font: inherit;
   font-size: 12px;
   font-weight: ${({ $current }) => ($current ? 700 : 500)};
   color: ${({ $current, $done }) => ($current ? colors.PRIMARY : $done ? colors.GRAY1 : colors.GRAY3)};
   cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
   border-bottom: 2px solid ${({ $current }) => ($current ? colors.PRIMARY : 'transparent')};
   &:disabled {
      opacity: ${({ $current }) => ($current ? 1 : 0.6)};
   }
   span {
      white-space: nowrap;
   }
   @media (max-width: 480px) {
      span {
         font-size: 11px;
      }
   }
`;

const Dot = styled.span<{ $current: boolean; $done: boolean }>`
   width: 24px;
   height: 24px;
   border-radius: 50%;
   display: inline-grid;
   place-items: center;
   font-size: 12px;
   font-weight: 700;
   background: ${({ $current, $done }) => ($current || $done ? colors.PRIMARY : colors.GRAY14)};
   color: ${({ $current, $done }) => ($current || $done ? '#fff' : colors.GRAY3)};
   border: 1px solid ${({ $current, $done }) => ($current || $done ? colors.PRIMARY : colors.GRAY13)};
`;

const Body = styled.div`
   padding: 18px 20px;
   display: grid;
   gap: 16px;
   overflow: auto;
   flex: 1;
`;

const Section = styled.div`
   display: grid;
   gap: 12px;
`;

const SecTitle = styled.h3`
   margin: 0;
   font-size: 12px;
   letter-spacing: 0.05em;
   text-transform: uppercase;
   color: ${colors.GRAY3};
`;

const Field = styled.div`
   display: grid;
   gap: 5px;
   min-width: 0;
   label {
      font-weight: 600;
      font-size: 13px;
   }
`;

const Hint = styled.span`
   color: ${colors.GRAY3};
   font-size: 12px;
`;

const fieldCss = css`
   border: 1px solid ${colors.GRAY13};
   background: ${colors.WHITE};
   color: ${colors.GRAY1};
   border-radius: 9px;
   padding: 8px 10px;
   font: inherit;
   font-size: 14px;
   width: 100%;
   box-sizing: border-box;
   &:focus-visible {
      outline: 3px solid ${colors.PRIMARY}40;
      outline-offset: 1px;
   }
`;
const Input = styled.input`${fieldCss}`;
const Select = styled.select`${fieldCss}`;
const Textarea = styled.textarea`
   ${fieldCss}
   font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
   text-transform: uppercase;
   resize: vertical;
`;

const Grid2 = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 12px;
   @media (max-width: 560px) {
      grid-template-columns: 1fr;
   }
`;
const Grid3 = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr 1fr;
   gap: 12px;
   @media (max-width: 560px) {
      grid-template-columns: 1fr;
   }
`;

const Toggle = styled.label<{ $small?: boolean }>`
   display: flex;
   align-items: center;
   gap: ${({ $small }) => ($small ? '7px' : '10px')};
   font-weight: 500;
   font-size: ${({ $small }) => ($small ? '12.5px' : '13.5px')};
   color: ${({ $small }) => ($small ? colors.GRAY3 : 'inherit')};
   cursor: pointer;
   white-space: nowrap;
   input {
      width: ${({ $small }) => ($small ? '15px' : '18px')};
      height: ${({ $small }) => ($small ? '15px' : '18px')};
      flex-shrink: 0;
   }
`;

/* Step 2: the FREE tick spans the modal; the three fields share ONE row
 * across the full width (two when FREE hides the price), stacking on phones. */
const FreeRow = styled(Toggle)`
   width: 100%;
`;
const RateRow = styled.div<{ $cols: 2 | 3 }>`
   display: grid;
   width: 100%;
   grid-template-columns: repeat(${({ $cols }) => $cols}, minmax(0, 1fr));
   gap: 12px;
   > * {
      min-width: 0;
   }
   @media (max-width: 560px) {
      grid-template-columns: 1fr;
   }
`;

/* Step 1: search + "Show offline vendos" on one line inside the list. */
const ListTools = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 8px 12px;
   border-bottom: 1px solid ${colors.GRAY13};
   flex-wrap: wrap;
`;

const ReachDot = styled.span<{ $reach: VendoReach }>`
   width: 8px;
   height: 8px;
   border-radius: 50%;
   flex-shrink: 0;
   display: inline-block;
   background: ${({ $reach }) => ($reach === 'online' ? colors.SUCCESS : $reach === 'degraded' ? '#f0b429' : colors.GRAY13)};
   box-shadow: 0 0 0 2px ${({ $reach }) => ($reach === 'online' ? `${colors.SUCCESS}33` : 'transparent')};
`;

const VendoList = styled.div`
   border: 1px solid ${colors.GRAY13};
   border-radius: 10px;
   max-height: 260px;
   overflow: auto;
   display: grid;
`;

const VendoRow = styled.label<{ $all?: boolean; $sub?: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 9px 12px;
   padding-left: ${({ $sub }) => ($sub ? '24px' : '12px')};
   border-bottom: 1px solid ${colors.GRAY13};
   cursor: pointer;
   font-weight: 500;
   font-size: 13.5px;
   background: ${({ $all }) => ($all ? `${colors.PRIMARY}14` : 'transparent')};
   &:last-child {
      border-bottom: 0;
   }
   input {
      width: 17px;
      height: 17px;
      flex-shrink: 0;
   }
   small {
      color: ${colors.GRAY3};
      margin-left: auto;
      font-size: 11.5px;
      white-space: nowrap;
   }
`;

const VendoSearch = styled.input`
   ${fieldCss}
   width: auto;
   flex: 1;
   min-width: 140px;
   border-radius: 8px;
   padding: 7px 10px;
`;

const Picked = styled.div`
   color: ${colors.GRAY3};
   font-size: 12.5px;
   b {
      color: ${colors.GRAY1};
   }
`;

/* The options share the full row equally — no empty space (Ray 2026-10-18). */
const Choices = styled.div`
   display: flex;
   gap: 6px;
   > * { flex: 1 1 0; min-width: 0; text-align: center; }
`;

const Choice = styled.button<{ $on: boolean }>`
   border: 1px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY13)};
   background: ${({ $on }) => ($on ? `${colors.PRIMARY}14` : colors.WHITE)};
   color: ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY1)};
   border-radius: 9px;
   padding: 8px 12px;
   cursor: pointer;
   font: inherit;
   font-size: 13px;
   font-weight: 600;
`;

const PreviewWrap = styled.div`
   background: ${colors.GRAY14};
   border: 1px dashed ${colors.GRAY13};
   border-radius: 10px;
   padding: 16px;
   display: flex;
   justify-content: center;
   overflow: auto;
`;

const DoneBox = styled.div`
   display: grid;
   gap: 4px;
   padding: 12px 14px;
   border-radius: 10px;
   background: ${colors.SUCCESS}14;
   border: 1px solid ${colors.SUCCESS}55;
   b {
      font-size: 15px;
   }
   span {
      font-size: 13px;
      color: ${colors.GRAY3};
   }
`;

const Foot = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
   gap: 10px;
   padding: 14px 20px;
   border-top: 1px solid ${colors.GRAY13};
   flex-wrap: wrap;
   flex-shrink: 0;
`;

const Summary = styled.span`
   color: ${colors.GRAY3};
   font-size: 13px;
   flex: 1;
   min-width: 160px;
`;

const Btns = styled.div`
   display: flex;
   gap: 8px;
`;

const btnCss = css`
   border: 1px solid ${colors.GRAY13};
   background: ${colors.WHITE};
   color: ${colors.GRAY1};
   padding: 9px 14px;
   border-radius: 9px;
   font: inherit;
   font-weight: 600;
   font-size: 13.5px;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;
const Ghost = styled.button`${btnCss}`;
const Primary = styled.button`
   ${btnCss}
   background: ${colors.PRIMARY};
   border-color: ${colors.PRIMARY};
   color: #fff;
`;

const LinkBtn = styled.button`
   border: 0;
   background: transparent;
   color: ${colors.PRIMARY};
   font: inherit;
   font-size: 12px;
   cursor: pointer;
   padding: 0;
   text-decoration: underline;
`;
